import type {
  BuilderElement,
  ElementType,
  Industry,
  Template,
} from '@/utils/builder/types';
import { generateId } from '@/utils/builder/id-utils';
import { createDefaultAccordionElement } from '@/utils/builder/default-config/accordionDefaults';
import { createDefaultTableElement } from '@/utils/builder/default-config/tableDefaults';
import { createDefaultGalleryElement } from '@/utils/builder/default-config/galleryDefaults';
import { createDefaultCarouselElement } from '@/utils/builder/default-config/carouselDefaults';
import { createDefaultFormElement } from '@/utils/builder/default-config/formDefaults';
import { createDefaultFormGroupElement } from '@/utils/builder/default-config/formGroupDefaults';

// Duplicate an element with new IDs (recursively handles children)
export const duplicateElement = (
  element: BuilderElement,
  oldTabIdToNewTabIdMap?: Map<string, string>
): BuilderElement => {
  const newElementId = generateId();
  let newTabConfig = element.tabConfig;

  if (element.type === 'tab-container' && element.tabConfig) {
    const newMap = new Map<string, string>();
    const newTabs = element.tabConfig.tabs.map((tab) => {
      const newTabId = generateId();
      newMap.set(tab.id, newTabId);
      return { ...tab, id: newTabId };
    });

    newTabConfig = {
      ...element.tabConfig,
      tabs: newTabs,
      activeTabId: newTabs[0]?.id,
    };
    oldTabIdToNewTabIdMap = newMap;
  }

  const normalizedChildren = Array.isArray(element.children)
    ? element.children
    : element.children
      ? [element.children]
      : [];

  const newElement: BuilderElement = {
    ...element,
    id: newElementId,
    tabConfig: newTabConfig,
    children: normalizedChildren.map((child) => {
      const duplicatedChild = duplicateElement(child, oldTabIdToNewTabIdMap);
      if (
        duplicatedChild.parentTabId &&
        oldTabIdToNewTabIdMap?.has(duplicatedChild.parentTabId)
      ) {
        duplicatedChild.parentTabId = oldTabIdToNewTabIdMap.get(
          duplicatedChild.parentTabId
        );
      }
      return duplicatedChild;
    }),
  };
  return newElement;
};

// Add an element to a container
export const addElementToContainer = (
  elements: BuilderElement[],
  containerId: string,
  newElement: BuilderElement,
  targetIndex?: number
): BuilderElement[] => {
  return elements.map((element) => {
    if (!element) return element;
    if (element.id === containerId) {
      const children = element.children || [];
      const newChildren = [...children];
      if (targetIndex !== undefined) {
        newChildren.splice(targetIndex, 0, newElement);
      } else {
        newChildren.push(newElement);
      }
      return { ...element, children: newChildren };
    }
    if (element.children) {
      return {
        ...element,
        children: addElementToContainer(
          element.children,
          containerId,
          newElement,
          targetIndex
        ),
      };
    }
    return element;
  });
};

// Update or replace an element in the structure
// Preserves children's _isNew and _needsSync flags from the original element
export const updateElement = (
  elements: BuilderElement[],
  id: string,
  updatedElement: BuilderElement
): BuilderElement[] => {
  return elements.map((element) => {
    if (element?.id === id) {
      // When updating an element, preserve children's _isNew and _needsSync flags
      // from the original element if they exist in the tree
      const normalizedElementChildren = Array.isArray(element.children)
        ? element.children
        : element.children
          ? [element.children]
          : [];
      const normalizedUpdatedChildren = Array.isArray(updatedElement.children)
        ? updatedElement.children
        : updatedElement.children
          ? [updatedElement.children]
          : [];

      if (normalizedElementChildren.length && normalizedUpdatedChildren.length) {
        const preservedChildren = normalizedUpdatedChildren.map((updatedChild) => {
          const originalChild = normalizedElementChildren.find((c) => c.id === updatedChild.id);
          if (originalChild) {
            // Preserve _isNew and _needsSync flags from original child
            return {
              ...updatedChild,
              _isNew: originalChild._isNew ?? updatedChild._isNew,
              _needsSync: originalChild._needsSync ?? updatedChild._needsSync,
            };
          }
          return updatedChild;
        });
        return {
          ...updatedElement,
          children: preservedChildren,
        };
      }
      return updatedElement;
    }
    if (element?.children) {
      const normalizedChildren = Array.isArray(element.children)
        ? element.children
        : [element.children];
      return {
        ...element,
        children: updateElement(normalizedChildren, id, updatedElement),
      };
    }
    return element;
  });
};

// Delete an element from the tree
export const deleteElement = (
  elements: BuilderElement[],
  id: string
): BuilderElement[] => {
  return elements
    .filter((element) => element.id !== id)
    .map((element) => {
      if (element.children) {
        return { ...element, children: deleteElement(element.children, id) };
      }
      return element;
    });
};

// Create a new element with default properties
export const createNewElement = (type: ElementType): BuilderElement => {
  const id = generateId();
  const baseElement: BuilderElement = { id, type, styles: {} };

  const createDefaultListItem = (index: number): BuilderElement => ({
    id: generateId(),
    type: 'listItem',
    content: `List item ${index + 1}`,
    styles: {
      marginBottom: '8px',
    },
  });

  // Complex elements with structured default content/config
  if (type === 'accordion') {
    return createDefaultAccordionElement();
  }
  if (type === 'table') {
    return createDefaultTableElement();
  }
  if (type === 'gallery') {
    return createDefaultGalleryElement();
  }
  if (type === 'carousel') {
    return createDefaultCarouselElement();
  }
  if (type === 'form') {
    return createDefaultFormElement();
  }
  if (type === 'form-group') {
    return createDefaultFormGroupElement();
  }

  const elementDefaults: Partial<Record<ElementType, Partial<BuilderElement>>> =
    {
      heading: {
        content: 'Heading',
        styles: {
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'forground',
          padding: '10px',
          margin: '0',
        },
      },
      paragraph: {
        content: 'This is a paragraph of text. Click to edit this text.',
        styles: {
          fontSize: '16px',
          color: 'primary',
          padding: '10px',
          margin: '0',
        },
      },
      button: {
        content: 'Click Me',
        styles: {
          backgroundColor: 'primary',
          color: '#FFFFFF',
          padding: '10px 20px',
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
        },
      },
      link: {
        content: 'Link',
        children: [],
        styles: {
          color: 'link',
        },
      },
      divider: {
        styles: {
          height: '1px',
          backgroundColor: '#E5E7EB',
          width: '100%',
          margin: '20px 0',
        },
      },
      container: {
        styles: {
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          minHeight: '100px',
        },
        children: [],
      },
      'video-container': {
        children: [],
        videoSettings: {
          autoplay: true,
          muted: true,
          loop: true,
          controls: false,
          playbackSpeed: 'normal',
        },
        styles: {
          backgroundColor: 'background',
          padding: '20px',
          margin: '10px 0',
          borderRadius: '4px',
          minHeight: '300px',
        },
      },
      image: {
        src: '',
        alt: 'Image',
        styles: { width: '100%', height: 'auto', objectFit: 'contain' },
      },
      list: {
        listTag: 'ul',
        children: [createDefaultListItem(0), createDefaultListItem(1), createDefaultListItem(2)],
        styles: {
          paddingLeft: '24px',
          margin: '0',
          listStyleType: 'disc',
          listStylePosition: 'outside',
        },
      },
      listItem: {
        content: 'List item',
        styles: {
          marginBottom: '8px',
        },
      },
    };

  return { ...baseElement, ...elementDefaults[type] };
};

// Create a new popup template
export const createNewPopupTemplate = (
  name: string,
  industry: Industry
): Template => {
  return {
    id: generateId(),
    name,
    industry,
    elements: [
      {
        id: generateId(),
        type: 'container',
        styles: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0, 0.6)',
          width: '100%',
          height: '100vh',
          zIndex: '1000',
        },
        children: [
          {
            id: generateId(),
            type: 'container',
            content: '',
            styles: {
              backgroundColor: 'white',
              padding: '20px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: '1000',
              minWidth: '300px',
              maxWidth: '500px',
            },
            children: [
              {
                id: generateId(),
                type: 'heading',
                content: 'Popup Title',
                styles: {
                  fontSize: '24px',
                  fontWeight: 'bold',
                  marginBottom: '16px',
                  textAlign: 'center',
                },
              },
              {
                id: generateId(),
                type: 'paragraph',
                content:
                  'This is a new popup window. You can customize it by adding elements and styling.',
                styles: {
                  fontSize: '16px',
                  lineHeight: '1.5',
                  marginBottom: '16px',
                  textAlign: 'center',
                },
              },
              {
                id: generateId(),
                type: 'button',
                content: 'Close',
                styles: {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                },
                interactionType: 'popup',
                triggerPopup: 'none',
              },
            ],
          },
        ],
      },
    ],
  };
};
